import React, { useEffect, useMemo, useState } from 'react';
import { SignInButton, UserButton, useAuth } from '@clerk/clerk-react';
import {
  ChartLine,
  CheckCircle,
  ClockCounterClockwise,
  Database,
  FilmSlate,
  Key,
  LinkSimple,
  Lightning,
  ListChecks,
  ShieldCheck,
  SpinnerGap,
  Trash,
  UploadSimple,
  WarningCircle,
} from '@phosphor-icons/react';
import Turnstile from 'react-turnstile';
import './VideoAnalyzer.css';

interface TranscriptionEntry {
  timestamp: string;
  text: string;
}

interface CaseStudyFrame {
  url: string;
  timestamp?: string;
  layout?: string;
}

interface CaseStudyHighlightGif {
  url: string;
  start_timestamp?: string;
  duration_seconds?: number;
}

interface CaseStudyContent {
  title: string;
  client_context: string;
  business_goal: string;
  challenge: string;
  strategy: string;
  creative_execution: string;
  results: string;
  why_it_matters: string;
  lead_hook: string;
  proof_points: string[];
}

interface VideoProcessingMeta {
  title?: string;
  channel?: string;
  duration_seconds?: number;
  duration_formatted?: string;
}

interface AnalysisResult {
  transcription: TranscriptionEntry[];
  topics: string[];
  visual_elements: string[];
  on_screen_text: string[];
  summary: string;
  timestamps: string[];
  inferred_client: string;
  inferred_industry: string;
  case_study: CaseStudyContent;
  case_study_frames: CaseStudyFrame[];
  case_study_highlight_gif?: CaseStudyHighlightGif;
  video_meta?: VideoProcessingMeta;
}

interface VideoMeta {
  title: string;
  author_name: string;
  thumbnail_url: string;
  video_url: string;
  duration?: string;
}

interface CreditsState {
  balance: number;
  daily?: {
    requests: number;
    credits_used: number;
    cap: number;
  };
  monthly?: {
    requests: number;
    credits_used: number;
    free_refill: number;
  };
}

interface HistoryItem {
  id: string;
  job_id: string;
  title: string;
  source_type: string;
  source_ref?: string;
  created_at: string;
}

interface VideoAnalyzerCoreProps {
  clerkEnabled: boolean;
  isSignedIn: boolean;
  getAuthToken?: () => Promise<string | null>;
}

const PROCESSING_STAGES: Array<{ title: string; detail: string }> = [
  {
    title: 'Safety Check',
    detail: 'Sign-in and anti-bot checks are completed first.',
  },
  {
    title: 'Job Queued',
    detail: 'Your request is queued in the worker pipeline.',
  },
  {
    title: 'Video Analysis',
    detail: 'Gemini reviews story, visuals, and messaging quality.',
  },
  {
    title: 'Case Study Assembly',
    detail: 'Output sections and media references are prepared.',
  },
  {
    title: 'Final Packaging',
    detail: 'Results are saved to history and returned to your workspace.',
  },
];

const FLOW_STEPS = [
  {
    title: 'Secure Intake',
    detail: 'Turnstile + auth checks run before any expensive processing.',
  },
  {
    title: 'Source Ingestion',
    detail: 'We ingest YouTube link metadata or uploaded file in R2.',
  },
  {
    title: 'Model Execution',
    detail: 'Worker executes Gemini pipeline using owner credits or BYOK.',
  },
  {
    title: 'Persistent Memory',
    detail: 'D1 stores history, credits, and case-study references.',
  },
];

const getApiBaseUrl = (): string => {
  return (process.env.REACT_APP_API_URL || 'http://localhost:8787').replace(/\/$/, '');
};

const resolveMediaUrl = (url: string): string => {
  if (!url) return '';
  if (url.startsWith('http://') || url.startsWith('https://')) return url;
  return `${getApiBaseUrl()}${url.startsWith('/') ? '' : '/'}${url}`;
};

const asRecord = (value: unknown): Record<string, unknown> => {
  return value && typeof value === 'object' ? (value as Record<string, unknown>) : {};
};

const asStringArray = (value: unknown): string[] => {
  if (!Array.isArray(value)) return [];
  return value.filter((item): item is string => typeof item === 'string' && item.trim().length > 0);
};

const sleep = (ms: number): Promise<void> => new Promise((resolve) => setTimeout(resolve, ms));

const formatElapsed = (seconds: number): string => {
  const minutes = Math.floor(seconds / 60);
  const remainder = seconds % 60;
  return `${minutes}m ${remainder.toString().padStart(2, '0')}s`;
};

const normalizeKnownError = (message: string): string => {
  const lowered = message.toLowerCase();

  if (lowered.includes('auth_required') || lowered.includes('please sign in')) {
    return 'Please sign in to analyze videos and save history.';
  }

  if (lowered.includes('byok_not_configured')) {
    return 'BYOK mode is selected, but your Gemini key is not saved yet.';
  }

  if (lowered.includes('credits_exhausted')) {
    return 'Owner-key credits are exhausted right now. Use BYOK or wait for refill.';
  }

  if (lowered.includes('daily_cap_reached')) {
    return 'Daily owner-key cap is reached. Use BYOK or come back tomorrow.';
  }

  if (lowered.includes('rate_limited') || lowered.includes('too many requests')) {
    return 'Too many requests right now. Wait a minute and retry.';
  }

  if (lowered.includes('turnstile_failed')) {
    return 'Bot verification failed. Please verify again and retry.';
  }

  if (lowered.includes('owner_key_disabled')) {
    return 'Owner key is temporarily disabled. Switch provider mode to BYOK.';
  }

  if (lowered.includes('quota') || lowered.includes('429')) {
    return 'The selected Gemini key hit quota/rate limits. Please retry shortly.';
  }

  if (lowered.includes('currently experiencing high demand') || lowered.includes('503')) {
    return 'Gemini is busy right now. Retry in 1-2 minutes.';
  }

  if (lowered.includes('job_timeout') || lowered.includes('exceeded processing timeout')) {
    return 'This job exceeded the processing limit. Retry with BYOK or use a shorter video.';
  }

  if (lowered.includes('upload is too large')) {
    return 'Uploaded file is larger than the current limit.';
  }

  return message;
};

const normalizeCaseStudy = (value: unknown): CaseStudyContent => {
  const record = asRecord(value);
  return {
    title: typeof record.title === 'string' ? record.title : '',
    client_context: typeof record.client_context === 'string' ? record.client_context : '',
    business_goal: typeof record.business_goal === 'string' ? record.business_goal : '',
    challenge: typeof record.challenge === 'string' ? record.challenge : '',
    strategy: typeof record.strategy === 'string' ? record.strategy : '',
    creative_execution:
      typeof record.creative_execution === 'string' ? record.creative_execution : '',
    results: typeof record.results === 'string' ? record.results : '',
    why_it_matters: typeof record.why_it_matters === 'string' ? record.why_it_matters : '',
    lead_hook: typeof record.lead_hook === 'string' ? record.lead_hook : '',
    proof_points: asStringArray(record.proof_points),
  };
};

const normalizeFrames = (value: unknown): CaseStudyFrame[] => {
  if (!Array.isArray(value)) return [];

  return value
    .filter((item): item is Record<string, unknown> => !!item && typeof item === 'object')
    .map((item) => {
      const url = typeof item.url === 'string' ? item.url : '';
      const timestamp = typeof item.timestamp === 'string' ? item.timestamp : '';
      const layout = typeof item.layout === 'string' ? item.layout : 'wide';
      return { url, timestamp, layout };
    })
    .filter((frame) => frame.url.length > 0);
};

const normalizeHighlightGif = (value: unknown): CaseStudyHighlightGif | undefined => {
  const record = asRecord(value);
  const url = typeof record.url === 'string' ? record.url : '';
  if (!url) return undefined;

  return {
    url,
    start_timestamp: typeof record.start_timestamp === 'string' ? record.start_timestamp : undefined,
    duration_seconds:
      typeof record.duration_seconds === 'number' ? record.duration_seconds : undefined,
  };
};

const normalizeAnalysisResult = (payload: unknown): AnalysisResult | null => {
  if (!payload || typeof payload !== 'object') return null;

  const record = payload as Record<string, unknown>;
  const rawTranscription = Array.isArray(record.transcription) ? record.transcription : [];

  const transcription: TranscriptionEntry[] = rawTranscription
    .filter((entry): entry is Record<string, unknown> => !!entry && typeof entry === 'object')
    .map((entry) => ({
      timestamp: typeof entry.timestamp === 'string' ? entry.timestamp : '00:00',
      text: typeof entry.text === 'string' ? entry.text : '',
    }))
    .filter((entry) => entry.text.trim().length > 0);

  const videoMetaRecord = asRecord(record.video_meta);

  return {
    transcription,
    topics: asStringArray(record.topics),
    visual_elements: asStringArray(record.visual_elements),
    on_screen_text: asStringArray(record.on_screen_text),
    summary: typeof record.summary === 'string' ? record.summary : '',
    timestamps: asStringArray(record.timestamps),
    inferred_client:
      typeof record.inferred_client === 'string' && record.inferred_client.trim()
        ? record.inferred_client.trim()
        : 'Unknown',
    inferred_industry:
      typeof record.inferred_industry === 'string' && record.inferred_industry.trim()
        ? record.inferred_industry.trim()
        : 'Unknown',
    case_study: normalizeCaseStudy(record.case_study),
    case_study_frames: normalizeFrames(record.case_study_frames),
    case_study_highlight_gif: normalizeHighlightGif(record.case_study_highlight_gif),
    video_meta: {
      title: typeof videoMetaRecord.title === 'string' ? videoMetaRecord.title : undefined,
      channel: typeof videoMetaRecord.channel === 'string' ? videoMetaRecord.channel : undefined,
      duration_seconds:
        typeof videoMetaRecord.duration_seconds === 'number'
          ? videoMetaRecord.duration_seconds
          : undefined,
      duration_formatted:
        typeof videoMetaRecord.duration_formatted === 'string'
          ? videoMetaRecord.duration_formatted
          : undefined,
    },
  };
};

const isMeaningfulValue = (value?: string): boolean => {
  if (!value) return false;
  const cleaned = value.trim().toLowerCase();
  return cleaned !== '' && cleaned !== 'n/a' && cleaned !== 'na' && cleaned !== 'unknown';
};

const extractYoutubeVideoId = (url: string): string => {
  try {
    const normalized = normalizeUrl(url);
    const parsed = new URL(normalized);
    const host = parsed.hostname.replace(/^www\./, '');

    if (host === 'youtu.be') {
      return parsed.pathname.split('/').filter(Boolean)[0] || '';
    }

    if (host === 'youtube.com' || host.endsWith('.youtube.com')) {
      const v = parsed.searchParams.get('v');
      if (v) return v;

      const parts = parsed.pathname.split('/').filter(Boolean);
      if ((parts[0] === 'shorts' || parts[0] === 'embed') && parts[1]) {
        return parts[1];
      }
    }
  } catch {
    return '';
  }

  return '';
};

const buildYoutubeThumbnailUrl = (url: string, fallback = ''): string => {
  const videoId = extractYoutubeVideoId(url);
  if (!videoId) return fallback;
  return `https://i.ytimg.com/vi/${videoId}/hqdefault.jpg`;
};

const fetchYoutubeMeta = async (url: string): Promise<VideoMeta | null> => {
  try {
    const oembedUrl = `https://www.youtube.com/oembed?url=${encodeURIComponent(url)}&format=json`;
    const res = await fetch(oembedUrl);
    if (!res.ok) return null;

    const data = await res.json();
    return {
      title: data.title,
      author_name: data.author_name,
      thumbnail_url: buildYoutubeThumbnailUrl(url, data.thumbnail_url),
      video_url: url,
      duration: undefined,
    };
  } catch {
    return null;
  }
};

const normalizeUrl = (url: string): string => {
  return url.startsWith('http') ? url : `https://${url}`;
};

const isValidYoutubeUrl = (url: string): boolean => {
  const youtubeRegex = /^(https?:\/\/)?(www\.)?(youtube\.com|youtu\.be)\/.+$/;
  return youtubeRegex.test(url);
};

const mergeVideoMeta = (
  base: VideoMeta | null,
  apiMeta: VideoProcessingMeta | undefined,
  normalizedUrl: string
): VideoMeta | null => {
  if (!base && !apiMeta) return null;

  return {
    title: apiMeta?.title || base?.title || 'Untitled video',
    author_name: apiMeta?.channel || base?.author_name || '',
    thumbnail_url: base?.thumbnail_url || '',
    video_url: base?.video_url || normalizedUrl,
    duration: apiMeta?.duration_formatted || base?.duration,
  };
};

const formatDateTime = (value: string): string => {
  if (!value) return '';
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return value;
  return date.toLocaleString();
};

const sourceLabel = (sourceType: string): string => {
  if (sourceType === 'youtube_url') return 'YouTube URL';
  if (sourceType === 'upload') return 'Uploaded file';
  return sourceType || 'Source';
};

const resolveInitialTab = (): 'analysis' | 'case' | 'history' => {
  if (typeof window === 'undefined') return 'analysis';
  const tab = new URLSearchParams(window.location.search).get('tab');
  if (tab === 'analysis' || tab === 'case' || tab === 'history') return tab;
  return 'analysis';
};

const formatCaseStudy = (meta: VideoMeta | null, analysis: AnalysisResult) => {
  const caseStudy = analysis.case_study;

  const clientName = isMeaningfulValue(analysis.inferred_client)
    ? analysis.inferred_client
    : 'Confidential client';
  const industryName = isMeaningfulValue(analysis.inferred_industry)
    ? analysis.inferred_industry
    : 'Not explicitly stated';
  const duration = analysis.video_meta?.duration_formatted || meta?.duration;

  const rawFrames: CaseStudyFrame[] =
    analysis.case_study_frames.length > 0
      ? analysis.case_study_frames
      : meta?.thumbnail_url
        ? [{ url: meta.thumbnail_url, timestamp: '', layout: 'full' }]
        : [];

  const hasFrames = rawFrames.length > 0;
  const getFrame = (index: number): CaseStudyFrame | null => {
    if (!hasFrames) return null;
    return rawFrames[index % rawFrames.length];
  };

  const renderFrame = (index: number, label: string, className: string) => {
    const frame = getFrame(index);
    if (!frame) return null;
    return (
      <figure className={`case-media ${className}`}>
        <img src={resolveMediaUrl(frame.url)} alt={label} width={1280} height={720} loading="lazy" />
        <figcaption>
          {label}
          {frame.timestamp ? ` · ${frame.timestamp}` : ''}
        </figcaption>
      </figure>
    );
  };

  const proofPoints =
    caseStudy.proof_points.length > 0
      ? caseStudy.proof_points
      : [
          'Narrative structure aligns with buyer decision journey.',
          'Visual hierarchy supports message clarity in first seconds.',
          'Creative direction supports trust and conversion confidence.',
        ];

  const highlightGif = analysis.case_study_highlight_gif;

  return (
    <div className="case-study">
      {highlightGif?.url && (
        <section className="case-section case-highlight">
          <figure className="case-highlight-media">
            <img
              src={resolveMediaUrl(highlightGif.url)}
              alt="5 second project highlight loop"
              width={1280}
              height={720}
              loading="lazy"
            />
            <figcaption>
              5s highlight loop
              {highlightGif.start_timestamp ? ` · starts at ${highlightGif.start_timestamp}` : ''}
            </figcaption>
          </figure>
        </section>
      )}

      <section className="case-section case-hero">
        <h2 className="case-study-title">
          {caseStudy.title || `${meta?.title || 'Campaign'}: Explainer strategy case study`}
        </h2>
        <div className="case-meta-row">
          <span>Client: {clientName}</span>
          <span>Industry: {industryName}</span>
          {duration && <span>Duration: {duration}</span>}
          {isMeaningfulValue(meta?.author_name) && <span>Channel: {meta?.author_name}</span>}
        </div>
        <p>
          {caseStudy.client_context ||
            'This project turns a complex value proposition into a clear story that drives audience action.'}
        </p>
      </section>

      <div className={`case-story-grid ${hasFrames ? '' : 'no-media'}`}>
        <section className="case-section">
          <h3>Business Objective</h3>
          <p>
            {caseStudy.business_goal ||
              'Improve audience understanding quickly and create stronger conversion intent.'}
          </p>
        </section>
        {renderFrame(0, 'Storyboard key frame', 'media-tall')}
      </div>

      <div className={`case-story-grid reverse ${hasFrames ? '' : 'no-media'}`}>
        {renderFrame(1, 'Message clarity frame', 'media-wide')}
        <section className="case-section">
          <h3>Strategic Challenge</h3>
          <p>
            {caseStudy.challenge ||
              'Translate complex messaging into a concise narrative that feels premium and memorable.'}
          </p>
        </section>
      </div>

      <div className={`case-story-grid ${hasFrames ? '' : 'no-media'}`}>
        <section className="case-section">
          <h3>Strategic Approach</h3>
          <p>
            {caseStudy.strategy ||
              'Structure the narrative around buyer friction points, then layer proof in a simple sequence.'}
          </p>
        </section>
        {hasFrames && (
          <div className="case-media-stack">
            {renderFrame(2, 'Style development frame', 'media-compact')}
            {renderFrame(3, 'Narrative transition frame', 'media-compact')}
          </div>
        )}
      </div>

      <div className={`case-story-grid reverse ${hasFrames ? '' : 'no-media'}`}>
        {hasFrames && (
          <div className="case-media-stack">
            {renderFrame(4, 'Illustration detail frame', 'media-compact')}
            {renderFrame(5, 'Final message frame', 'media-compact')}
          </div>
        )}
        <section className="case-section">
          <h3>Creative Execution</h3>
          <p>
            {caseStudy.creative_execution ||
              'Script pacing, illustration transitions, and hierarchy are aligned to keep attention and comprehension high.'}
          </p>
        </section>
      </div>

      <section className="case-section">
        <h3>Performance Signals</h3>
        <ul className="proof-list">
          {proofPoints.map((point, idx) => (
            <li key={idx}>{point}</li>
          ))}
        </ul>
      </section>

      <section className="case-section">
        <h3>Outcome Narrative</h3>
        <p>
          {caseStudy.results ||
            analysis.summary ||
            'The video now delivers clearer positioning, better comprehension, and stronger confidence at decision time.'}
        </p>
      </section>

      <section className="case-section case-cta">
        <h3>What This Unlocks</h3>
        <p>
          {caseStudy.why_it_matters ||
            'This method can be reused for teams who need storytelling quality and measurable conversion impact.'}
        </p>
        <p className="lead-hook">
          {caseStudy.lead_hook ||
            'If your product story is strong but your current video is not converting, this blueprint is your next move.'}
        </p>
      </section>
    </div>
  );
};

const VideoAnalyzerCore: React.FC<VideoAnalyzerCoreProps> = ({
  clerkEnabled,
  isSignedIn,
  getAuthToken,
}) => {
  const [videoUrl, setVideoUrl] = useState('');
  const [selectedFile, setSelectedFile] = useState<File | null>(null);
  const [inputType, setInputType] = useState<'youtube_url' | 'upload'>('youtube_url');
  const [providerMode, setProviderMode] = useState<'owner' | 'byok'>('owner');
  const [turnstileToken, setTurnstileToken] = useState('');

  const [isLoading, setIsLoading] = useState(false);
  const [jobStatus, setJobStatus] = useState('');
  const [analysisResult, setAnalysisResult] = useState<AnalysisResult | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [videoMeta, setVideoMeta] = useState<VideoMeta | null>(null);
  const [activeTab, setActiveTab] = useState<'analysis' | 'case' | 'history'>(resolveInitialTab);
  const [progressStep, setProgressStep] = useState(0);
  const [progressValue, setProgressValue] = useState(8);
  const [elapsedSeconds, setElapsedSeconds] = useState(0);

  const [credits, setCredits] = useState<CreditsState | null>(null);
  const [historyItems, setHistoryItems] = useState<HistoryItem[]>([]);
  const [historyLoading, setHistoryLoading] = useState(false);
  const [byokInput, setByokInput] = useState('');
  const [byokStatus, setByokStatus] = useState<string | null>(null);

  const turnstileSiteKey = process.env.REACT_APP_TURNSTILE_SITE_KEY || '';

  const authHeaders = async (includeJson = true): Promise<Record<string, string>> => {
    const headers: Record<string, string> = includeJson ? { 'Content-Type': 'application/json' } : {};

    if (clerkEnabled && getAuthToken) {
      const token = await getAuthToken();
      if (token) {
        headers.Authorization = `Bearer ${token}`;
      }
    }

    return headers;
  };

  const parseApiError = async (response: Response): Promise<string> => {
    const raw = await response.text();
    try {
      const parsed = JSON.parse(raw);
      const record = asRecord(parsed);
      const message = typeof record.error === 'string' ? record.error : raw;
      const code = typeof record.code === 'string' ? ` (${record.code})` : '';
      return normalizeKnownError(`${message}${code}`);
    } catch {
      return normalizeKnownError(raw || 'Request failed.');
    }
  };

  const refreshCredits = async () => {
    if (!isSignedIn) return;

    const response = await fetch(`${getApiBaseUrl()}/v1/credits`, {
      method: 'GET',
      headers: await authHeaders(false),
    });

    if (!response.ok) {
      throw new Error(await parseApiError(response));
    }

    const payload = await response.json();
    setCredits(payload as CreditsState);
  };

  const loadHistory = async () => {
    if (!isSignedIn) return;

    setHistoryLoading(true);
    try {
      const response = await fetch(`${getApiBaseUrl()}/v1/history?limit=20`, {
        method: 'GET',
        headers: await authHeaders(false),
      });

      if (!response.ok) {
        throw new Error(await parseApiError(response));
      }

      const payload = await response.json();
      const items = Array.isArray(payload.items) ? payload.items : [];
      setHistoryItems(
        items.map((item: Record<string, unknown>) => ({
          id: String(item.id || ''),
          job_id: String(item.job_id || ''),
          title: String(item.title || 'Untitled analysis'),
          source_type: String(item.source_type || ''),
          source_ref: typeof item.source_ref === 'string' ? item.source_ref : undefined,
          created_at: String(item.created_at || ''),
        }))
      );
    } finally {
      setHistoryLoading(false);
    }
  };

  useEffect(() => {
    if (!isSignedIn) {
      setCredits(null);
      setHistoryItems([]);
      return;
    }

    refreshCredits().catch(() => {
      setCredits(null);
    });

    loadHistory().catch(() => {
      setHistoryItems([]);
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isSignedIn]);

  useEffect(() => {
    if (typeof window === 'undefined') return;
    const url = new URL(window.location.href);
    url.searchParams.set('tab', activeTab);
    window.history.replaceState({}, '', url.toString());
  }, [activeTab]);

  const handleUrlBlur = async () => {
    if (inputType !== 'youtube_url') return;

    const normalizedUrl = normalizeUrl(videoUrl.trim());
    if (!isValidYoutubeUrl(normalizedUrl)) {
      setVideoMeta(null);
      return;
    }

    const meta = await fetchYoutubeMeta(normalizedUrl);
    if (meta) {
      setVideoMeta(meta);
    }
  };

  const uploadVideoIfNeeded = async (): Promise<string | null> => {
    if (inputType !== 'upload') return null;
    if (!selectedFile) {
      throw new Error('Please select an MP4 or WebM file.');
    }

    if (!['video/mp4', 'video/webm'].includes(selectedFile.type)) {
      throw new Error('Only MP4 and WebM files are supported right now.');
    }

    const initResponse = await fetch(`${getApiBaseUrl()}/v1/uploads/init`, {
      method: 'POST',
      headers: await authHeaders(true),
      body: JSON.stringify({
        filename: selectedFile.name,
        content_type: selectedFile.type,
        size_bytes: selectedFile.size,
      }),
    });

    if (!initResponse.ok) {
      throw new Error(await parseApiError(initResponse));
    }

    const initPayload = asRecord(await initResponse.json());
    const uploadId = typeof initPayload.upload_id === 'string' ? initPayload.upload_id : '';
    const uploadUrl = typeof initPayload.upload_url === 'string' ? initPayload.upload_url : '';

    if (!uploadId || !uploadUrl) {
      throw new Error('Upload session failed to initialize.');
    }

    setJobStatus('Uploading file to secure storage…');

    const uploadResponse = await fetch(`${getApiBaseUrl()}${uploadUrl}`, {
      method: 'PUT',
      headers: await authHeaders(false),
      body: selectedFile,
    });

    if (!uploadResponse.ok) {
      throw new Error(await parseApiError(uploadResponse));
    }

    const completeResponse = await fetch(`${getApiBaseUrl()}/v1/uploads/complete`, {
      method: 'POST',
      headers: await authHeaders(true),
      body: JSON.stringify({ upload_id: uploadId }),
    });

    if (!completeResponse.ok) {
      throw new Error(await parseApiError(completeResponse));
    }

    return uploadId;
  };

  const pollForJobResult = async (jobId: string): Promise<unknown> => {
    const maxAttempts = 240;
    const pollIntervalMs = 2000;

    for (let attempt = 0; attempt < maxAttempts; attempt += 1) {
      await sleep(pollIntervalMs);

      setProgressStep((prev) => (prev < PROCESSING_STAGES.length - 1 ? prev + 1 : prev));
      setProgressValue((prev) => (prev < 94 ? Math.min(prev + 2, 94) : prev));

      const response = await fetch(`${getApiBaseUrl()}/v1/jobs/${jobId}/result`, {
        method: 'GET',
        headers: await authHeaders(false),
      });

      if (!response.ok) {
        throw new Error(await parseApiError(response));
      }

      const payload = asRecord(await response.json());
      const status = typeof payload.status === 'string' ? payload.status : 'queued';

      if (status === 'completed') {
        return payload.result;
      }

      if (status === 'failed') {
        throw new Error('Video analysis failed while processing.');
      }

      const elapsed = Math.round(((attempt + 1) * pollIntervalMs) / 1000);
      setJobStatus(`Job status: ${status} · elapsed ${formatElapsed(elapsed)}`);
    }

    const statusResponse = await fetch(`${getApiBaseUrl()}/v1/jobs/${jobId}`, {
      method: 'GET',
      headers: await authHeaders(false),
    });

    if (statusResponse.ok) {
      const statusPayload = asRecord(await statusResponse.json());
      const status = typeof statusPayload.status === 'string' ? statusPayload.status : 'processing';
      const backendMessage =
        typeof statusPayload.error_message === 'string' ? statusPayload.error_message : '';
      if (status === 'failed' && backendMessage) {
        throw new Error(backendMessage);
      }
      throw new Error(
        `The job is still ${status} after ~8 minutes. It may be delayed upstream. Retry with BYOK or try a shorter video.`
      );
    }

    throw new Error('Job took too long to finish. Retry with BYOK or a shorter video.');
  };

  const createAndRunJob = async (uploadId: string | null, normalizedUrl: string): Promise<AnalysisResult> => {
    const body: Record<string, unknown> = {
      input_type: inputType,
      provider_mode: providerMode,
      turnstile_token: turnstileToken,
    };

    if (inputType === 'youtube_url') {
      body.youtube_url = normalizedUrl;
    } else {
      body.upload_id = uploadId;
    }

    const createJobResponse = await fetch(`${getApiBaseUrl()}/v1/jobs`, {
      method: 'POST',
      headers: await authHeaders(true),
      body: JSON.stringify(body),
    });

    if (!createJobResponse.ok) {
      throw new Error(await parseApiError(createJobResponse));
    }

    const createPayload = asRecord(await createJobResponse.json());
    const jobId = typeof createPayload.job_id === 'string' ? createPayload.job_id : '';

    if (!jobId) {
      throw new Error('Job was created without a valid ID.');
    }

    setJobStatus('Job queued. Waiting for analysis…');

    const resultPayload = await pollForJobResult(jobId);
    const normalizedData = normalizeAnalysisResult(resultPayload);

    if (!normalizedData) {
      throw new Error('Unexpected response format from server.');
    }

    return normalizedData;
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setError(null);
    setByokStatus(null);
    setAnalysisResult(null);

    if (clerkEnabled && !isSignedIn) {
      setError('Please sign in first to run analysis and save history.');
      return;
    }

    const normalizedUrl = normalizeUrl(videoUrl.trim());
    if (inputType === 'youtube_url' && !isValidYoutubeUrl(normalizedUrl)) {
      setError('Please enter a valid YouTube URL.');
      return;
    }

    if (!turnstileToken.trim()) {
      setError('Please complete bot verification first.');
      return;
    }

    setIsLoading(true);
    setJobStatus('Preparing request…');
    setProgressStep(0);
    setProgressValue(10);
    setElapsedSeconds(0);

    let progressTimer: ReturnType<typeof setInterval> | null = setInterval(() => {
      setProgressStep((prev) => (prev < PROCESSING_STAGES.length - 1 ? prev + 1 : prev));
      setProgressValue((prev) => (prev < 80 ? Math.min(prev + 4, 80) : prev));
      setElapsedSeconds((prev) => prev + 2);
    }, 2000);

    try {
      let localMeta = videoMeta;
      if (inputType === 'youtube_url' && !localMeta) {
        localMeta = await fetchYoutubeMeta(normalizedUrl);
        if (localMeta) setVideoMeta(localMeta);
      }

      const uploadId = await uploadVideoIfNeeded();
      const normalizedData = await createAndRunJob(uploadId, normalizedUrl);

      const mergedMeta =
        inputType === 'youtube_url'
          ? mergeVideoMeta(localMeta, normalizedData.video_meta, normalizedUrl)
          : videoMeta;

      if (mergedMeta) setVideoMeta(mergedMeta);

      setProgressValue(100);
      setJobStatus('Analysis complete.');
      setAnalysisResult(normalizedData);
      setActiveTab('analysis');

      await refreshCredits();
      await loadHistory();
    } catch (err) {
      const message = err instanceof Error ? err.message : 'An error occurred.';
      const userFriendlyMessage =
        message.toLowerCase() === 'failed to fetch'
          ? 'Cannot reach the API server. Please start the Cloudflare Worker and try again.'
          : message;
      setError(normalizeKnownError(userFriendlyMessage));
    } finally {
      if (progressTimer) {
        clearInterval(progressTimer);
        progressTimer = null;
      }
      setIsLoading(false);
    }
  };

  const handleSaveByok = async () => {
    setByokStatus(null);
    setError(null);

    if (!byokInput.trim()) {
      setByokStatus('Paste a Gemini key before saving.');
      return;
    }

    try {
      const response = await fetch(`${getApiBaseUrl()}/v1/byok`, {
        method: 'POST',
        headers: await authHeaders(true),
        body: JSON.stringify({ provider: 'gemini', api_key: byokInput.trim() }),
      });

      if (!response.ok) {
        throw new Error(await parseApiError(response));
      }

      setByokInput('');
      setByokStatus('BYOK key saved securely.');
    } catch (err) {
      const message = err instanceof Error ? err.message : 'Failed to save BYOK key.';
      setByokStatus(normalizeKnownError(message));
    }
  };

  const handleRemoveByok = async () => {
    setByokStatus(null);

    try {
      const response = await fetch(`${getApiBaseUrl()}/v1/byok`, {
        method: 'DELETE',
        headers: await authHeaders(false),
      });

      if (!response.ok) {
        throw new Error(await parseApiError(response));
      }

      setByokStatus('BYOK key removed.');
    } catch (err) {
      const message = err instanceof Error ? err.message : 'Failed to remove BYOK key.';
      setByokStatus(normalizeKnownError(message));
    }
  };

  const handleDeleteHistory = async (historyId: string) => {
    try {
      const response = await fetch(`${getApiBaseUrl()}/v1/history/${historyId}`, {
        method: 'DELETE',
        headers: await authHeaders(false),
      });

      if (!response.ok) {
        throw new Error(await parseApiError(response));
      }

      setHistoryItems((prev) => prev.filter((item) => item.id !== historyId));
    } catch (err) {
      const message = err instanceof Error ? err.message : 'Failed to delete history item.';
      setError(normalizeKnownError(message));
    }
  };

  const hasUsefulDuration = isMeaningfulValue(analysisResult?.video_meta?.duration_formatted);
  const hasUsefulClient = isMeaningfulValue(analysisResult?.inferred_client);
  const hasUsefulIndustry = isMeaningfulValue(analysisResult?.inferred_industry);

  const canSubmit = useMemo(() => {
    if (isLoading) return false;
    if (!isSignedIn && clerkEnabled) return false;
    if (!turnstileToken.trim()) return false;
    if (inputType === 'upload') return Boolean(selectedFile);
    return Boolean(videoUrl.trim());
  }, [clerkEnabled, inputType, isLoading, isSignedIn, selectedFile, turnstileToken, videoUrl]);

  const inputProcessingHint =
    inputType === 'upload'
      ? 'Uploaded videos usually finish in 2-6 minutes depending on file size.'
      : 'Most YouTube links finish in about 1-3 minutes.';

  const providerModeHint =
    providerMode === 'owner'
      ? 'Recommended: use app credits for a one-click experience.'
      : 'Advanced: use your own Gemini billing if you need higher volume.';

  return (
    <div className="analysis-workspace">
      <aside className="control-rail">
        <header className="brand-block">
          <p className="brand-kicker">Cloudflare-native Video Intelligence</p>
          <h1>VidInsight Control Room</h1>
          <p>
            Analyze YouTube links or uploaded files with secure credits, optional BYOK, and
            case-study history.
          </p>
        </header>

        <section className="rail-card auth-card">
          <div className="card-title-row">
            <ShieldCheck size={18} weight="duotone" aria-hidden="true" />
            <h2>Identity</h2>
          </div>
          {clerkEnabled ? (
            isSignedIn ? (
              <div className="auth-live-row">
                <p>Signed in and ready for protected actions.</p>
                <UserButton />
              </div>
            ) : (
              <div className="auth-live-row is-signed-out">
                <p>Sign in to use credits, BYOK, and history.</p>
                <SignInButton mode="modal">
                  <button type="button" className="outline-button">
                    Sign In
                  </button>
                </SignInButton>
              </div>
            )
          ) : (
            <p>Auth is disabled in this environment.</p>
          )}
        </section>

        {isSignedIn && (
          <section className="rail-card">
            <div className="card-title-row">
              <ChartLine size={18} weight="duotone" aria-hidden="true" />
              <h2>Credits</h2>
            </div>
            {credits ? (
              <>
                <p className="metric-line">
                  Balance <strong>{credits.balance}</strong>
                </p>
                <div className="meter-block">
                  <label>Daily usage</label>
                  <div className="meter-track">
                    <span
                      style={{
                        width: `${Math.min(
                          100,
                          ((credits.daily?.credits_used || 0) / Math.max(1, credits.daily?.cap || 1)) *
                            100
                        )}%`,
                      }}
                    />
                  </div>
                  <small>
                    {credits.daily?.credits_used || 0}/{credits.daily?.cap || 0}
                  </small>
                </div>
                <div className="meter-block">
                  <label>Monthly usage</label>
                  <div className="meter-track">
                    <span
                      style={{
                        width: `${Math.min(
                          100,
                          ((credits.monthly?.credits_used || 0) /
                            Math.max(1, credits.monthly?.free_refill || 1)) *
                            100
                        )}%`,
                      }}
                    />
                  </div>
                  <small>
                    {credits.monthly?.credits_used || 0}/{credits.monthly?.free_refill || 0}
                  </small>
                </div>
              </>
            ) : (
              <div className="skeleton-lines" aria-hidden>
                <span />
                <span />
                <span />
              </div>
            )}
          </section>
        )}

        {isSignedIn && (
          <section className="rail-card">
            <div className="card-title-row">
              <Key size={18} weight="duotone" aria-hidden="true" />
              <h2>Provider</h2>
            </div>

            <label htmlFor="provider-mode-select">Provider mode</label>
            <select
              id="provider-mode-select"
              className="control-input"
              name="provider_mode"
              autoComplete="off"
              value={providerMode}
              onChange={(e) => setProviderMode(e.target.value as 'owner' | 'byok')}
            >
              <option value="owner">Use owner key (credit-based)</option>
              <option value="byok">Use my Gemini key (BYOK)</option>
            </select>
            <p className="mode-hint">{providerModeHint}</p>

            <div className="mode-brief-grid" aria-hidden="true">
              <div className={`mode-brief-card ${providerMode === 'owner' ? 'is-active' : ''}`}>
                <b>Owner mode</b>
                <span>Uses your in-app credits. No extra setup.</span>
              </div>
              <div className={`mode-brief-card ${providerMode === 'byok' ? 'is-active' : ''}`}>
                <b>BYOK mode</b>
                <span>Uses your own Gemini key and billing plan.</span>
              </div>
            </div>

            {providerMode === 'byok' && (
              <>
                <label htmlFor="byok-key">Gemini key</label>
                <input
                  id="byok-key"
                  type="text"
                  className="control-input"
                  placeholder="Paste your Gemini key…"
                  name="byok_key"
                  autoComplete="off"
                  spellCheck={false}
                  value={byokInput}
                  onChange={(e) => setByokInput(e.target.value)}
                />

                <div className="inline-actions">
                  <button type="button" className="solid-button" onClick={handleSaveByok}>
                    Save BYOK
                  </button>
                  <button type="button" className="outline-button" onClick={handleRemoveByok}>
                    Remove
                  </button>
                </div>

                {byokStatus && (
                  <p
                    aria-live="polite"
                    className={`inline-message ${byokStatus.toLowerCase().includes('failed') ? 'is-error' : 'is-ok'}`}
                  >
                    {byokStatus}
                  </p>
                )}
              </>
            )}
          </section>
        )}

        <section className="rail-card flow-card">
          <div className="card-title-row">
            <Database size={18} weight="duotone" aria-hidden="true" />
            <h2>Dataflow</h2>
          </div>
          <ol>
            {FLOW_STEPS.map((step, index) => (
              <li key={step.title} style={{ animationDelay: `${index * 90}ms` }}>
                <strong>{step.title}</strong>
                <p>{step.detail}</p>
              </li>
            ))}
          </ol>
        </section>

        {isSignedIn && (
          <section className="rail-card verification-card">
            <div className="card-title-row">
              <ShieldCheck size={18} weight="duotone" aria-hidden="true" />
              <h2>Bot Verification</h2>
            </div>
            <label>{turnstileSiteKey ? 'Turnstile challenge' : 'Turnstile token (manual)'}</label>
            {turnstileSiteKey ? (
              <div className="turnstile-wrap">
                <Turnstile
                  sitekey={turnstileSiteKey}
                  onVerify={(token) => setTurnstileToken(token)}
                  onExpire={() => setTurnstileToken('')}
                  onError={() => setTurnstileToken('')}
                />
              </div>
            ) : (
              <input
                type="text"
                className="control-input"
                placeholder="Paste turnstile token…"
                name="turnstile_token"
                autoComplete="off"
                value={turnstileToken}
                onChange={(e) => setTurnstileToken(e.target.value)}
              />
            )}
          </section>
        )}
      </aside>

      <main className="studio-area">
        <section className="studio-card intake-card">
          <div className="card-title-row">
            <FilmSlate size={18} weight="duotone" aria-hidden="true" />
            <h2>Input Studio</h2>
          </div>

          <form onSubmit={handleSubmit} className="input-form">
            <div className="field-group">
              <label htmlFor="input-type">Input type</label>
              <select
                id="input-type"
                className="control-input"
                name="input_type"
                autoComplete="off"
                value={inputType}
                onChange={(e) => setInputType(e.target.value as 'youtube_url' | 'upload')}
              >
                <option value="youtube_url">YouTube URL</option>
                <option value="upload">Upload video file</option>
              </select>
            </div>

            {inputType === 'youtube_url' ? (
              <div className="field-group">
                <label htmlFor="youtube-url">YouTube URL</label>
                <div className="input-with-icon">
                  <LinkSimple size={18} weight="duotone" aria-hidden="true" />
                  <input
                    id="youtube-url"
                    type="url"
                    value={videoUrl}
                    onChange={(e) => setVideoUrl(e.target.value)}
                    onBlur={handleUrlBlur}
                    placeholder="https://www.youtube.com/watch?v=…"
                    className="control-input"
                    name="youtube_url"
                    autoComplete="off"
                    inputMode="url"
                    spellCheck={false}
                  />
                </div>
                <small>Use full URL to improve metadata fetch reliability.</small>
              </div>
            ) : (
              <div className="field-group">
                <label htmlFor="video-file">Video file</label>
                <div className="input-with-icon">
                  <UploadSimple size={18} weight="duotone" aria-hidden="true" />
                  <input
                    id="video-file"
                    type="file"
                    accept="video/mp4,video/webm"
                    className="control-input"
                    name="video_file"
                    onChange={(e) => {
                      const file = e.target.files && e.target.files[0] ? e.target.files[0] : null;
                      setSelectedFile(file);
                      setVideoMeta(null);
                    }}
                  />
                </div>
                <small>Supported formats: MP4, WebM.</small>
              </div>
            )}

            <div className="input-footer">
              <button type="submit" className="solid-button action-button" disabled={!canSubmit}>
                {isLoading ? (
                  <span>
                    <SpinnerGap size={16} className="spin" aria-hidden="true" />
                    Processing…
                  </span>
                ) : (
                  <span>
                    <Lightning size={16} weight="duotone" aria-hidden="true" />
                    Analyze Video
                  </span>
                )}
              </button>
              <p>
                Owner mode uses app credits. BYOK mode uses the user key. Turnstile is mandatory for
                analysis requests.
              </p>
            </div>
          </form>
        </section>

        {videoMeta && (
          <section className="studio-card preview-card">
            <div className="card-title-row">
              <PlayIcon />
              <h2>Video Snapshot</h2>
            </div>
            <div className="preview-layout">
              {videoMeta.thumbnail_url ? (
                <img
                  className="video-thumb"
                  src={videoMeta.thumbnail_url}
                  alt="Video thumbnail"
                  width={1280}
                  height={720}
                  loading="lazy"
                />
              ) : (
                <div className="video-meta-empty-thumb">Preview unavailable</div>
              )}

              <div className="preview-copy">
                <h3>
                  <a href={videoMeta.video_url} target="_blank" rel="noopener noreferrer">
                    {videoMeta.title}
                  </a>
                </h3>
                {isMeaningfulValue(videoMeta.author_name) && (
                  <p>Source channel: {videoMeta.author_name}</p>
                )}
                <p>On analyze, the app reviews story quality, visual craft, and conversion potential.</p>
              </div>
            </div>
          </section>
        )}

        {isLoading && (
          <section className="studio-card processing-card" role="status" aria-live="polite">
            <div className="card-title-row">
              <SpinnerGap size={18} className="spin" aria-hidden="true" />
              <h2>Processing Timeline</h2>
            </div>
            <p className="processing-intro">
              {inputProcessingHint} Elapsed time: <b>{formatElapsed(elapsedSeconds)}</b>
            </p>

            <div className="processing-bar" aria-label="Processing progress">
              <div className="processing-fill" style={{ width: `${progressValue}%` }} />
            </div>

            <ul className="processing-list">
              {PROCESSING_STAGES.map((stage, index) => {
                const isDone = index < progressStep;
                const isCurrent = index === progressStep;
                return (
                  <li
                    key={stage.title}
                    className={isDone ? 'is-done' : isCurrent ? 'is-current' : ''}
                  >
                    {isDone ? (
                      <CheckCircle size={16} weight="fill" aria-hidden="true" />
                    ) : (
                      <ClockCounterClockwise size={16} aria-hidden="true" />
                    )}
                    <span>
                      <b>{stage.title}</b>
                      <small>{stage.detail}</small>
                    </span>
                  </li>
                );
              })}
            </ul>

            {jobStatus && <p className="status-note">{jobStatus}</p>}
            {elapsedSeconds >= 240 && (
              <p className="status-note status-note-warning">
                This run is taking longer than usual. Keep this tab open, or retry with BYOK for
                higher limits.
              </p>
            )}
          </section>
        )}

        {error && (
          <div className="inline-message is-error major-error" aria-live="polite">
            <WarningCircle size={18} weight="duotone" aria-hidden="true" />
            <span>{error}</span>
          </div>
        )}

        <section className="studio-card result-card">
          <div className="tab-header" role="tablist" aria-label="Result views">
            <button
              type="button"
              role="tab"
              aria-selected={activeTab === 'analysis'}
              className={activeTab === 'analysis' ? 'tab-active' : ''}
              onClick={() => setActiveTab('analysis')}
            >
              Analysis
            </button>
            <button
              type="button"
              role="tab"
              aria-selected={activeTab === 'case'}
              className={activeTab === 'case' ? 'tab-active' : ''}
              onClick={() => setActiveTab('case')}
            >
              Case Study
            </button>
            <button
              type="button"
              role="tab"
              aria-selected={activeTab === 'history'}
              className={activeTab === 'history' ? 'tab-active' : ''}
              onClick={() => setActiveTab('history')}
            >
              History
            </button>
          </div>

          {activeTab === 'analysis' && (
            <div className="analysis-results">
              {!analysisResult ? (
                <div className="empty-state">
                  <ListChecks size={24} weight="duotone" aria-hidden="true" />
                  <h3>No analysis yet</h3>
                  <p>Run a video to view summary, topics, transcript, and visual findings here.</p>
                </div>
              ) : (
                <>
                  {(hasUsefulClient || hasUsefulIndustry || hasUsefulDuration) && (
                    <section>
                      <h3>AI Inference Snapshot</h3>
                      <div className="meta-pill-row">
                        {hasUsefulClient && (
                          <span className="meta-pill">Client: {analysisResult.inferred_client}</span>
                        )}
                        {hasUsefulIndustry && (
                          <span className="meta-pill">Industry: {analysisResult.inferred_industry}</span>
                        )}
                        {hasUsefulDuration && (
                          <span className="meta-pill">
                            Duration: {analysisResult.video_meta?.duration_formatted}
                          </span>
                        )}
                      </div>
                    </section>
                  )}

                  <section>
                    <h3>Summary</h3>
                    <p>{analysisResult.summary || 'No summary returned.'}</p>
                  </section>

                  <section>
                    <h3>Key Topics</h3>
                    <ul>
                      {analysisResult.topics.length > 0 ? (
                        analysisResult.topics.map((topic, idx) => <li key={idx}>{topic}</li>)
                      ) : (
                        <li>No topics identified.</li>
                      )}
                    </ul>
                  </section>

                  <section>
                    <h3>Transcription</h3>
                    <ul className="transcription-list">
                      {analysisResult.transcription.length > 0 ? (
                        analysisResult.transcription.map((entry, idx) => (
                          <li key={idx}>
                            <b>[{entry.timestamp}]</b> {entry.text}
                          </li>
                        ))
                      ) : (
                        <li>No transcription returned.</li>
                      )}
                    </ul>
                  </section>

                  <section>
                    <h3>Visual Elements</h3>
                    <ul>
                      {analysisResult.visual_elements.length > 0 ? (
                        analysisResult.visual_elements.map((el, idx) => <li key={idx}>{el}</li>)
                      ) : (
                        <li>No visual elements returned.</li>
                      )}
                    </ul>
                  </section>

                  <section>
                    <h3>On-Screen Text</h3>
                    <ul>
                      {analysisResult.on_screen_text.length > 0 ? (
                        analysisResult.on_screen_text.map((txt, idx) => <li key={idx}>{txt}</li>)
                      ) : (
                        <li>No on-screen text returned.</li>
                      )}
                    </ul>
                  </section>
                </>
              )}
            </div>
          )}

          {activeTab === 'case' && (
            <div className="analysis-results">
              {analysisResult ? (
                formatCaseStudy(videoMeta, analysisResult)
              ) : (
                <div className="empty-state">
                  <FilmSlate size={24} weight="duotone" aria-hidden="true" />
                  <h3>Case study will appear here</h3>
                  <p>After analysis, this tab shows campaign narrative, proof points, and frame references.</p>
                </div>
              )}
            </div>
          )}

          {activeTab === 'history' && (
            <div className="analysis-results">
              <section>
                <h3>Saved Analysis History</h3>
                {historyLoading ? (
                  <div className="skeleton-lines" aria-hidden>
                    <span />
                    <span />
                    <span />
                  </div>
                ) : historyItems.length === 0 ? (
                  <div className="empty-state compact">
                    <ClockCounterClockwise size={20} weight="duotone" aria-hidden="true" />
                    <p>No history yet. Run an analysis and it will be stored here automatically.</p>
                  </div>
                ) : (
                  <ul className="history-list">
                    {historyItems.map((item) => (
                      <li key={item.id}>
                        <div>
                          <b>{item.title}</b>
                          <small>
                            {sourceLabel(item.source_type)} · {formatDateTime(item.created_at)}
                          </small>
                        </div>
                        <button
                          type="button"
                          className="danger-button"
                          onClick={() => {
                            const shouldDelete = window.confirm(
                              'Delete this history entry permanently?'
                            );
                            if (shouldDelete) {
                              handleDeleteHistory(item.id);
                            }
                          }}
                        >
                          <Trash size={14} aria-hidden="true" />
                          Delete
                        </button>
                      </li>
                    ))}
                  </ul>
                )}
              </section>
            </div>
          )}
        </section>
      </main>
    </div>
  );
};

const PlayIcon: React.FC = () => <Lightning size={18} weight="duotone" aria-hidden="true" />;

const VideoAnalyzerWithClerk: React.FC = () => {
  const { isSignedIn, getToken } = useAuth();

  return (
    <VideoAnalyzerCore
      clerkEnabled={true}
      isSignedIn={Boolean(isSignedIn)}
      getAuthToken={() => getToken()}
    />
  );
};

const VideoAnalyzerWithoutClerk: React.FC = () => {
  return <VideoAnalyzerCore clerkEnabled={false} isSignedIn={true} />;
};

const VideoAnalyzer: React.FC = () => {
  const clerkEnabled = Boolean(process.env.REACT_APP_CLERK_PUBLISHABLE_KEY);
  return clerkEnabled ? <VideoAnalyzerWithClerk /> : <VideoAnalyzerWithoutClerk />;
};

export default VideoAnalyzer;
